import { FC, useState } from 'react';
import {
  Button,
  MultiSelect,
  Select,
  SelectItem,
  TextInput,
} from '@carbon/react';
import { Add, TrashCan } from '@carbon/icons-react';
import { organizationTypes } from 'components/entity-form/types';
import type { EntityFormSuffix, SuffixMode } from './types';
import styles from './page.module.scss';

// A monotonic client-only row id. Keying a suffix row on its array index attached
// each stateful child (the variants ListInput, the org-type MultiSelect) to the
// wrong row after a middle-row removal; a stable id created with the row fixes it.
let suffixClientIdCounter = 0;
export const suffixClientId = (): string =>
  `suffix-row-${(suffixClientIdCounter += 1)}`;

// A new group starts at "none", not "any". Mode decides whether G1 may suppress a
// suffix/organization_type conflict, and the spec's fail-safe is that an entry
// nobody has classified suppresses rather than falls open (AC-184).
export const blankSuffix = (): EntityFormSuffix => ({
  clientId: suffixClientId(),
  canonical: '',
  variants: [],
  mode: 'none',
  organizationTypes: [],
});

const MODE_OPTIONS: { value: SuffixMode; label: string }[] = [
  { value: 'specific', label: 'Specific' },
  { value: 'none', label: 'None' },
  { value: 'any', label: 'Any' },
];

const toList = (text: string): string[] =>
  text
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

const sameList = (a: string[], b: string[]) =>
  a.length === b.length && a.every((item, i) => item === b[i]);

interface ListInputProps {
  id: string;
  labelText: string;
  disabled?: boolean;
  value: string[];
  onChange: (value: string[]) => void;
}

// A comma-separated list, edited as text. It has to hold its own text because
// parsing on every keystroke and re-rendering `value.join(', ')` would eat the
// separator the moment it was typed — "Corporation, Trust" collapsed into one
// entry. The parent still only ever sees a parsed array.
const ListInput: FC<ListInputProps> = ({ value, onChange, ...rest }) => {
  const [text, setText] = useState(() => value.join(', '));
  const [lastValue, setLastValue] = useState(value);

  // Resync on an external change (Discard changes, a fresh load) but not on the
  // echo of our own edit, which would fight the cursor.
  if (value !== lastValue) {
    setLastValue(value);
    if (!sameList(toList(text), value)) setText(value.join(', '));
  }

  return (
    <TextInput
      {...rest}
      hideLabel
      value={text}
      onChange={(event) => {
        setText(event.target.value);
        onChange(toList(event.target.value));
      }}
    />
  );
};

interface OrganizationTypeSelectProps {
  index: number;
  canonical: string;
  disabled: boolean;
  value: string[];
  onChange: (value: string[]) => void;
}

// The candidate's organization_type is compared against this list, so the
// options are the same ones the entity create form writes — a free-text box let
// an SME type a value no entity could ever hold. A saved value outside the list
// is still offered (and stays selected) rather than silently dropped on save.
const OrganizationTypeSelect: FC<OrganizationTypeSelectProps> = ({
  index,
  canonical,
  disabled,
  value,
  onChange,
}) => {
  const label = `Organization types for ${canonical || `row ${index + 1}`}`;
  const unknown = value.filter(
    (v) => !organizationTypes.includes(v as (typeof organizationTypes)[number])
  );
  const items = [...organizationTypes, ...unknown];

  return (
    <MultiSelect
      id={`suffix-org-types-${index}`}
      titleText={label}
      label={value.length ? `${value.length} selected` : 'Any of…'}
      hideLabel
      items={items}
      itemToString={(item) => item ?? ''}
      selectedItems={value}
      disabled={disabled}
      onChange={({ selectedItems }) => onChange(selectedItems ?? [])}
      // Preserves the saved order rather than re-sorting on every change.
      sortItems={(itemsToSort) => [...itemsToSort]}
    />
  );
};

interface Props {
  suffixes: EntityFormSuffix[];
  onChange: (suffixes: EntityFormSuffix[]) => void;
}

// Replaces a free-text box that could only express canonical + variants. Mode and
// organization types had no text form, so they were carried over by canonical-name
// lookup — renaming a canonical silently dropped both, and every new group was
// written as "any" regardless of what the server would have defaulted it to.
export const SuffixEditor: FC<Props> = ({ suffixes, onChange }) => {
  const update = (index: number, changes: Partial<EntityFormSuffix>) =>
    onChange(
      suffixes.map((row, i) => (i === index ? { ...row, ...changes } : row))
    );

  const remove = (index: number) =>
    onChange(suffixes.filter((_row, i) => i !== index));

  return (
    <div className={styles.suffixEditor}>
      <table className={styles.suffixTable}>
        <thead>
          <tr>
            <th scope="col">Canonical</th>
            <th scope="col">Variants</th>
            <th scope="col">Compatibility</th>
            <th scope="col">Organization types</th>
            <th scope="col">
              <span className={styles.visuallyHidden}>Remove</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {suffixes.map((row, index) => (
            <tr key={row.clientId ?? index}>
              <td>
                <TextInput
                  id={`suffix-canonical-${index}`}
                  labelText={`Canonical form, row ${index + 1}`}
                  hideLabel
                  placeholder="LLC"
                  value={row.canonical}
                  onChange={(event) =>
                    update(index, { canonical: event.target.value })
                  }
                />
              </td>
              <td>
                <ListInput
                  id={`suffix-variants-${index}`}
                  labelText={`Variants for ${
                    row.canonical || `row ${index + 1}`
                  }`}
                  value={row.variants}
                  onChange={(variants) => update(index, { variants })}
                />
              </td>
              <td>
                <Select
                  id={`suffix-mode-${index}`}
                  labelText={`Compatibility for ${
                    row.canonical || `row ${index + 1}`
                  }`}
                  hideLabel
                  value={row.mode}
                  onChange={(event) =>
                    update(index, { mode: event.target.value as SuffixMode })
                  }
                >
                  {MODE_OPTIONS.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      text={option.label}
                    />
                  ))}
                </Select>
              </td>
              <td>
                <OrganizationTypeSelect
                  index={index}
                  canonical={row.canonical}
                  // Only "specific" consults the list; the other two modes answer
                  // without it, so offering the field would imply it mattered.
                  disabled={row.mode !== 'specific'}
                  value={row.organizationTypes ?? []}
                  onChange={(organizationTypes) =>
                    update(index, { organizationTypes })
                  }
                />
              </td>
              <td>
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={TrashCan}
                  iconDescription={`Remove ${
                    row.canonical || `row ${index + 1}`
                  }`}
                  onClick={() => remove(index)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className={styles.suffixHelp}>
        <strong>Compatibility</strong> — <em>Specific</em>: the
        candidate&rsquo;s organization type must be one of the listed types,
        anything else suppresses. <em>None</em>: always suppresses. <em>Any</em>
        : never suppresses. A new group starts at None.
      </p>
      <Button
        kind="tertiary"
        size="sm"
        renderIcon={Add}
        onClick={() => onChange([...suffixes, blankSuffix()])}
      >
        Add suffix group
      </Button>
    </div>
  );
};
